import { useId } from 'react';

import type { IntelligenceReport } from '../api/intelligenceReportSchemas';
import type { IntelligenceInsight, IntelligencePersona } from '../api/schemas';
import { AdvisorOrb } from '../features/intelligence/AdvisorOrb';
import { reportDateLabel, reviewedLine, signerOf } from '../lib/intelligence-view';
import { EmptyState } from './EmptyState';
import { InsightCard, InsightRow, keepTimesTogether, LeadInsight } from './IntelligenceInsight';

const STAMP_ORB = 40;
const ADVISOR_ORB = 28;
/** Past this many characters the director's line steps down from display size. */
const LONG_VOICE_CHARS = 200;

/** One stored report in full: who wrote it and when, what was read, then the briefing. */
export function IntelligenceReportView({ report }: { report: IntelligenceReport }) {
  const headlineId = useId();
  const date = reportDateLabel(report.createdAt, new Date());
  const { signer } = report;

  return (
    <>
      {/* The stamp: who went and looked, when, and what they read. The headline
          names the region for screen readers; the lead card shows it on screen. */}
      <section className="section intel-stamp-section" aria-labelledby={headlineId}>
        <h2 className="sr-only" id={headlineId}>{keepTimesTogether(report.headline)}</h2>
        <div className="intel-stamp">
          <AdvisorOrb personaId={signer.personaId} size={STAMP_ORB} />
          <div className="intel-stamp-text">
            <p className="intel-stamp-who">{`${signer.name} · ${signer.title}`}</p>
            <p className="intel-stamp-meta">{`${date ? `${date} · ` : ''}${reviewedLine(report.reviewed, report.range)}`}</p>
          </div>
        </div>
        {report.notice ? <p className="notice intel-notice" role="note">{report.notice}</p> : null}
      </section>

      {report.insights.length === 0 ? (
        <EmptyState
          title="Aún no hay suficientes datos para sugerencias."
          description="Vuelve en unas semanas cuando el negocio tenga más movimiento."
        />
      ) : null}

      {report.insights.length > 0 && report.personas.length > 0 ? (
        <Briefing insights={report.insights} personas={report.personas} />
      ) : null}

      {report.insights.length > 0 && report.personas.length === 0 ? (
        <section className="section card-list" aria-label="Sugerencias">
          {report.insights.map((insight, index) => (
            <InsightCard key={insightKey(insight, index)} insight={insight} />
          ))}
        </section>
      ) : null}
    </>
  );
}

/**
 * The first finding leads with the director's line and a clear button, the
 * rest sit below signed by the asesor who owns them, and the asesores close
 * the page.
 */
function Briefing({ insights, personas }: { insights: IntelligenceInsight[]; personas: IntelligencePersona[] }) {
  const leadId = useId();
  const restId = useId();
  const advisorsId = useId();
  const [lead, ...rest] = insights;
  const director = personas.find((persona) => persona.personaId === 'direccion');
  const advisors = personas.filter((persona) => persona.personaId !== 'direccion');
  if (!lead) return null;

  return (
    <>
      <section className="section" aria-labelledby={leadId}>
        <h2 className="section-label" id={leadId}>Empieza por esto</h2>
        <article className="card insight-lead">
          {director?.narration ? (
            <div className="insight-voice">
              <p className="insight-voice-line" data-long={director.narration.length > LONG_VOICE_CHARS || undefined}>
                {keepTimesTogether(director.narration)}
              </p>
              <p className="insight-byline">{`${director.name} · ${director.title}`}</p>
            </div>
          ) : null}
          <LeadInsight insight={lead} />
        </article>
      </section>

      {rest.length > 0 ? (
        <section className="section" aria-labelledby={restId}>
          <h2 className="section-label" id={restId}>Lo demás</h2>
          <div className="group rows insight-rows">
            {rest.map((insight, index) => (
              <InsightRow key={insightKey(insight, index)} insight={insight} signer={signerOf(insight, personas)} />
            ))}
          </div>
        </section>
      ) : null}

      {advisors.length > 0 ? (
        <section className="section" aria-labelledby={advisorsId}>
          <h2 className="section-label" id={advisorsId}>Tus asesores</h2>
          <div className="group rows insight-advisors">
            {advisors.map((persona) => (
              <div className="row intel-advisor" key={persona.personaId}>
                <AdvisorOrb personaId={persona.personaId} size={ADVISOR_ORB} />
                <div className="grow">
                  <p className="row-title">
                    {persona.name} <span className="insight-byline">{persona.title}</span>
                  </p>
                  <p className="row-sub">{keepTimesTogether(persona.narration ?? persona.quietNote ?? '')}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function insightKey(insight: IntelligenceInsight, index: number): string {
  const gap = insight.metrics.gap;
  return `${insight.type}-${typeof gap === 'string' ? gap : index}`;
}
